import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { REGION_GEO, REGION_ORDER } from "../lib/jawselRegions";
import { pingKunjungan } from "../lib/presence";
import { NAMES, viewFor, withCam, pathD } from "../lib/pulau/geo";
import { drawPulauFrame } from "../lib/pulau/render";
import { usePulauGestures } from "../lib/pulau/usePulauGestures";

// Home — pulau melayang DIMIRINGKAN (pseudo-isometrik) dengan batas resmi antar kota.
// Label nama & titik hitam dihapus; area klik mengikuti batas wilayah via SVG path transparan.
// Geometri/gambar ada di lib/pulau/{geo,sprites,cliff,render}.js
const FONT = "'Plus Jakarta Sans', sans-serif";

export default function Home() {
  const [cities, setCities] = useState([]);
  useEffect(() => { pingKunjungan(null); }, []);   // pengunjung unik harian (pulau)
  const [stats, setStats] = useState({});
  const [hoverId, setHoverId] = useState(null);
  const [view, setView] = useState(() => viewFor(window.innerWidth, window.innerHeight));
  const { cam, camRef, movedRef, handlers } = usePulauGestures();
  const canvasRef = useRef(null);
  const hoverRef = useRef(null);
  const statsRef = useRef({});
  const nav = useNavigate();

  useEffect(() => {
    api.publicCities().then(d => setCities(d.cities || [])).catch(() => {});
  }, []);
  useEffect(() => {
    Promise.all(REGION_ORDER.map(id =>
      api.publicCity(id).then(d => [id, d.state || {}]).catch(() => [id, {}])
    )).then(rows => setStats(Object.fromEntries(rows)));
  }, []);
  useEffect(() => {
    const onResize = () => setView(viewFor(window.innerWidth, window.innerHeight));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const list = useMemo(() => {
    const fallback = REGION_ORDER.map(id => ({ city_id: id, city_name: NAMES[id] }));
    const src = cities.length ? cities : fallback;
    return src
      .filter(c => REGION_GEO[c.city_id])
      .map(c => ({ ...c, name: c.city_name || NAMES[c.city_id] || c.city_id }))
      .sort((a, b) => REGION_ORDER.indexOf(a.city_id) - REGION_ORDER.indexOf(b.city_id));
  }, [cities]);

  useEffect(() => { hoverRef.current = hoverId; }, [hoverId]);
  useEffect(() => { statsRef.current = stats; }, [stats]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf = 0;
    function frame(ms) {
      raf = requestAnimationFrame(frame);
      drawPulauFrame(canvas, ctx, ms, { cam: camRef.current, hover: hoverRef.current, stats: statsRef.current });
    }
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []); // eslint-disable-line

  const svgView = withCam(view, cam, window.innerWidth, window.innerHeight);

  return (
    <div
      style={{ position: "fixed", inset: 0, overflow: "hidden", background: "#efece0", color: "#141414", fontFamily: FONT, touchAction: "none" }}
      {...handlers}
    >
      <canvas ref={canvasRef} data-testid="home-map-canvas" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 6 }} aria-hidden={false}>
        {list.map(c => (
          <path
            key={c.city_id}
            data-testid={`city-${c.city_id}`}
            d={pathD(REGION_GEO[c.city_id].pts, svgView)}
            fill="rgba(0,0,0,0)"
            stroke="none"
            pointerEvents="all"
            cursor="pointer"
            tabIndex={0}
            role="link"
            aria-label={`Buka kota ${c.name}`}
            onClick={() => {
              if (movedRef.current) { movedRef.current = false; return; }
              nav(`/kota/${c.city_id}`);
            }}
            onKeyDown={e => { if (e.key === "Enter" || e.key === " ") nav(`/kota/${c.city_id}`); }}
            onMouseEnter={() => setHoverId(c.city_id)}
            onMouseLeave={() => setHoverId(null)}
            onFocus={() => setHoverId(c.city_id)}
            onBlur={() => setHoverId(null)}
          />
        ))}
      </svg>
    </div>
  );
}
